import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { MatButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatOption } from '@angular/material/core';
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerToggle,
} from '@angular/material/datepicker';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatError, MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { dsDtos, userDtos, webDtos, webEnums } from '@vectronic/openApi-customer';
import { DateFormats, DateHelper, Translator } from '@vectronic/util';
import moment, { Moment } from 'moment';
import { CardComponent } from '../../components/card/card.component';
import { ColorPickerComponent } from '../../components/color-picker/color-picker.component';
import { sortbyOrderAscending } from '../../utils/util.sort';

@Component({
  selector: 'vectronic-animal-deployment',
  templateUrl: './animal-deployment.component.html',
  styleUrls: ['./animal-deployment.component.scss'],
  imports: [
    CommonModule,
    CardComponent,
    MatDialogContent,
    ReactiveFormsModule,
    FormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatTooltip,
    MatDialogActions,
    MatButton,
    MatDialogClose,
    MatIcon,
    MatRadioModule,
    MatCheckbox,
    ColorPickerComponent,
    MatError,
  ],
  providers: [provideMomentDateAdapter(new DateFormats().returnInternationalDateFormat())],
})
export class AnimalDeploymentComponent implements OnInit {
  dialogRef = inject(MatDialogRef<AnimalDeploymentComponent>);
  private fb = inject(FormBuilder);
  data: {
    collar?: webDtos.CollarExtended;
    animal?: webDtos.AnimalExtended;
    deployment?: userDtos.Deployment;
    collars: webDtos.CollarExtended[];
    animals: webDtos.AnimalExtended[];
    animalSpeciesOptions: dsDtos.AnimalSpecies[];
    type: 'edit' | 'add' | 'assignCollar' | 'assignAnimal';
  } = inject(MAT_DIALOG_DATA);

  form: FormGroup;
  maxDate = new Date();
  sexOptions: webEnums.AnimalSex[] = ['MALE', 'FEMALE', 'UNKNOWN'];
  ageOptions: webEnums.Age[] = [
    'SIX_MONTHS',
    'YEARLING',
    'TWO_TO_NINE_YEARS',
    'TEN_TO_FIFTEEN_YEARS',
    'OVER_FIFTEEN_YEARS',
  ];
  endTypeOptions: webEnums.EndType[] = [
    'FALL_OFF',
    'DEAD',
    'REMOVED',
    'DROP_OFF_ACTIVATED',
    'REDEPLOYED',
  ];

  @ViewChild('startInput', { read: MatInput })
  startInput: MatInput;
  @ViewChild('endInput', { read: MatInput })
  endInput: MatInput;

  result: {
    deployment?: webDtos.Deployment;
    animal?: webDtos.AnimalExtended;
    applyCollarRoles?: boolean;
    type: 'edit' | 'add' | 'assignCollar' | 'assignAnimal';
  };

  constructor() {
    const isCollarSelectionRequired =
      (this.data.type === 'assignCollar' || this.data.type === 'add') && !this.data.collar;
    const isAnimalSelectionRequired = this.data.type === 'assignAnimal' && !this.data.animal;
    this.form = this.fb.group({
      collarId: [null, isCollarSelectionRequired ? Validators.required : []],
      animalId: [null, isAnimalSelectionRequired ? Validators.required : []],
      species: [null, Validators.required],
      name: ['', Validators.required],
      sex: [null],
      selectedColor: [null],
      dateOfBirth: [null],
      dateOfDeath: [null],
      deathComment: [''],
      description: [''],
      personalNotes: [''],
      startDate: [null, Validators.required],
      age: [null],
      weight: [null, [Validators.min(0.01), this.numericInputValidator]],
      latitude: [null, [Validators.min(-90), Validators.max(90), this.numericInputValidator]],
      longitude: [null, [Validators.min(-180), Validators.max(180), this.numericInputValidator]],
      deployPerson: [''],
      deployComment: [''],
      shareWithCollarUsers: [false],
      closeDeployment: [false],
      endDate: [null],
      endType: [null],
      endLatitude: [null, [Validators.min(-90), Validators.max(90), this.numericInputValidator]],
      endLongitude: [null, [Validators.min(-180), Validators.max(180), this.numericInputValidator]],
      endPerson: [''],
    });

    this.form.get('startDate')?.valueChanges.subscribe((newStartDatePickerDate?: Date | Moment) => {
      if (!newStartDatePickerDate) return;
      const newStartDate = this.toIsoDayStart(newStartDatePickerDate);
      const otherDeployments = this.getOtherDeployments();
      const lastStartDate = DateHelper.deploymentsLastStartDate(otherDeployments);
      if (newStartDate < lastStartDate) this.form.get('closeDeployment')?.setValue(true);
    });

    // Add conditional validation for end based on closeDeployment
    this.form.get('closeDeployment')?.valueChanges.subscribe((close) => {
      if (close) this.form.get('endDate')?.setValidators(Validators.required);
      else this.form.get('endDate')?.clearValidators();
      this.form.get('endDate')?.updateValueAndValidity();
    });

    const { animal, deployment } = this.data;
    if (
      deployment?.endDate ||
      deployment?.endPerson ||
      deployment?.endType ||
      deployment?.endLatitude ||
      deployment?.endLongitude
    ) {
      this.form.get('closeDeployment')?.setValue(true);
    }

    if (animal?.role === 'READER') {
      //only happens in edit: it does not have to be set dynamically
      this.form.get('species')?.disable();
      this.form.get('name')?.disable();
      this.form.get('sex')?.disable();
      this.form.get('dateOfBirth')?.disable();
      this.form.get('dateOfDeath')?.disable();
      this.form.get('deathComment')?.disable();
      this.form.get('description')?.disable();
    }
  }

  ngOnInit() {
    if (this.data.animalSpeciesOptions) {
      this.data.animalSpeciesOptions = [...this.data.animalSpeciesOptions].sort((a, b) =>
        (a.english || '').localeCompare(b.english || ''),
      );
    }
    if (this.data.collars) {
      this.data.collars = [...this.data.collars].sort(sortbyOrderAscending);
    }
    if (this.data.animals) {
      this.data.animals = [...this.data.animals].sort(sortbyOrderAscending);
    }
    if (this.data.animal) {
      this.setFormInputValues(this.data.animal);
    }
  }

  isAnimalReader() {
    return this.data.animal?.role === 'READER';
  }

  private toIsoDayStart(dateValue?: Date | Moment | null) {
    if (!dateValue) return;
    return moment.isMoment(dateValue)
      ? DateHelper.datePickerMomentToIsoDayStart(dateValue)
      : DateHelper.datePickerDateToIsoDayStart(dateValue);
  }

  private toIsoDayEnd(dateValue?: Date | Moment | null) {
    if (!dateValue) return;
    return moment.isMoment(dateValue)
      ? DateHelper.datePickerMomentToIsoDayEnd(dateValue)
      : DateHelper.datePickerDateToIsoDayEnd(dateValue);
  }

  private getDatePickerDate(type: 'dateOfBirth' | 'dateOfDeath' | 'endDate' | 'startDate') {
    if (type === 'endDate') return this.toIsoDayEnd(this.form.get(type)?.value);
    return this.toIsoDayStart(this.form.get(type)?.value);
  }

  dateOfDeathFilter = (datePickerDate: Date | Moment) => {
    const dateOfDeath = this.toIsoDayStart(datePickerDate);
    const dateOfBirth = this.getDatePickerDate('dateOfBirth');
    const isAfterBirth = !dateOfBirth || dateOfDeath >= dateOfBirth;
    const animalDeployments = this.getAninmalDeployments();
    const lastStartDate = DateHelper.deploymentsLastStartDate(animalDeployments);
    const isAfterLastDeploymentStart = !lastStartDate || lastStartDate <= dateOfDeath;
    const startDate = this.getDatePickerDate('startDate');
    const isAfterSelectedStartDate = !startDate || startDate <= dateOfDeath;
    return isAfterBirth && isAfterLastDeploymentStart && isAfterSelectedStartDate;
  };

  dateOfBirthFilter = (datePickerDate: Date | Moment) => {
    const dateOfBirth = this.toIsoDayStart(datePickerDate);
    const dateOfDeath = this.getDatePickerDate('dateOfDeath');
    const isBeforeDeath = !dateOfDeath || dateOfBirth <= dateOfDeath;
    const animalDeployments = this.getAninmalDeployments();
    const firstStartDate = DateHelper.deploymentsFirstStartDate(animalDeployments);
    const isBeforeFirstDeployment = !firstStartDate || firstStartDate >= dateOfBirth;
    const startDate = this.getDatePickerDate('startDate');
    const isBeforeSelectedStartDate = !startDate || startDate >= dateOfBirth;
    return isBeforeDeath && isBeforeFirstDeployment && isBeforeSelectedStartDate;
  };

  private getAninmalDeployments() {
    const { animal, animals } = this.data;
    const animalId = this.form.get('animalId')?.value;
    return (animal ?? animals.find(({ id }) => id === animalId))?.deployments ?? [];
  }
  private getCollarDeployments() {
    const { collar, collars } = this.data;
    const collarId = this.form.get('collarId')?.value;
    return (collar ?? collars.find(({ id }) => id === collarId))?.deployments ?? [];
  }

  private getOtherDeployments() {
    const animalDeployments = this.getAninmalDeployments();
    const collarDeployments = this.getCollarDeployments();
    return [...animalDeployments, ...collarDeployments].filter(
      ({ id }) => id !== this.data.deployment?.id,
    );
  }

  dateFilterStart = (datePickerDate: Date | Moment) => {
    const startDate = this.toIsoDayStart(datePickerDate);
    const dateOfBirth = this.getDatePickerDate('dateOfBirth');
    if (dateOfBirth && startDate < dateOfBirth) return false;

    const dateOfDeath = this.getDatePickerDate('dateOfDeath');
    if (dateOfDeath && startDate > dateOfDeath) return false;

    const endDate = this.getDatePickerDate('endDate');
    const otherDeployments = this.getOtherDeployments();
    return DateHelper.isValidStartDate(startDate, endDate, otherDeployments);
  };

  dateFilterEnd = (datePickerDate: Date | Moment) => {
    const endDate = this.toIsoDayEnd(datePickerDate);
    const dateOfBirth = this.getDatePickerDate('dateOfBirth');
    if (dateOfBirth && endDate < dateOfBirth) return false;

    const dateOfDeath = this.getDatePickerDate('dateOfDeath');
    if (!dateOfBirth && dateOfDeath && endDate > dateOfDeath) return false;

    const startDate = this.getDatePickerDate('startDate');
    const otherDeployments = this.getOtherDeployments();
    return DateHelper.isValidEndDate(endDate, startDate, otherDeployments);
  };

  resetDates(newDeployments: webDtos.Deployment[]) {
    const startDate = this.getDatePickerDate('startDate');
    const isStartDateSelected = Boolean(startDate);
    const needToResetStartDate =
      isStartDateSelected && DateHelper.isDateInDeployments(startDate, newDeployments);
    if (needToResetStartDate) this.form.get('startDate')?.setValue(null);
    const endDate = this.getDatePickerDate('endDate');
    const isEndDateSelected = Boolean(endDate);
    const needToResetEndDate =
      isEndDateSelected && DateHelper.hasOverlap({ startDate, endDate }, newDeployments);
    if (needToResetEndDate) this.form.get('endDate')?.setValue(null);
  }

  updateNameOfNewAnimalFromSpeciesAndCollarId(collarId: number) {
    const species = this.form.get('species')?.value;
    if (species) {
      this.form.get('name')?.setValue(`${species.english} ${collarId}`);
    } else {
      this.form.get('name')?.setValue(`Animal for Collar ${collarId}`);
    }
  }

  onCollarSelectionChange(event: MatSelectChange) {
    const selectedCollar = this.data.collars.find(({ id }) => id === event.value);
    this.resetDates(selectedCollar?.deployments ?? []);
    if (selectedCollar && this.isNewAnimal()) {
      this.updateNameOfNewAnimalFromSpeciesAndCollarId(selectedCollar.id);
    }
    if (event.value === 0) this.form.get('collarId')?.setValue(null);
  }

  onAnimalSelectionChange(event: MatSelectChange) {
    const selectedAnimal = this.data.animals.find(({ id }) => id === event.value);
    this.resetDates(selectedAnimal?.deployments ?? []);
    this.setFormInputValues(selectedAnimal ?? { name: '' });
    if (event.value === 0) {
      if (this.data.collar) {
        this.updateNameOfNewAnimalFromSpeciesAndCollarId(this.data.collar.id);
      }
      this.form.get('animalId')?.setValue(0);
    }
  }

  private setFormInputValues(selectedAnimal: webDtos.AnimalExtended) {
    this.form.patchValue({
      collarId: this.data.deployment?.collarId,
      animalId: selectedAnimal.id,
      name: selectedAnimal.name,
      description: selectedAnimal.description,
      deathComment: selectedAnimal.deathComment,
      personalNotes: selectedAnimal.label,
      sex: selectedAnimal.animalSex,
      selectedColor: selectedAnimal.colorRgbCode,
      dateOfBirth: DateHelper.isoToDatePickerDate(selectedAnimal.dateOfBirth),
      dateOfDeath: DateHelper.isoToDatePickerDate(selectedAnimal.dateOfDeath),
      species: this.data.animalSpeciesOptions?.find((s) => s.id === selectedAnimal.animalSpeciesId),
      age: this.data.deployment?.age,
      weight: this.data.deployment?.weight,
      latitude: this.data.deployment?.startLatitude,
      longitude: this.data.deployment?.startLongitude,
      deployPerson: this.data.deployment?.startPerson,
      endPerson: this.data.deployment?.endPerson,
      deployComment: this.data.deployment?.comment,
      endType: this.data.deployment?.endType,
      endLatitude: this.data.deployment?.endLatitude,
      endLongitude: this.data.deployment?.endLongitude,
      startDate: DateHelper.isoToDatePickerDate(this.data.deployment?.startDate),
      endDate: DateHelper.isoToDatePickerDate(this.data.deployment?.endDate),
    });
  }

  onSubmit() {
    if (!this.form.valid) return;

    const formValue = this.form.value;
    const orginialAnimal =
      this.data.animal ?? this.data.animals.find((animal) => animal.id == formValue.animalId) ?? {};
    const updatedAnimal: webDtos.AnimalExtended = {
      ...orginialAnimal,
      name: formValue.name,
      description: formValue.description,
      deathComment: formValue.deathComment,
      label: formValue.personalNotes,
      animalSex: formValue.sex,
      colorRgbCode: formValue.selectedColor,
      dateOfBirth: this.toIsoDayStart(formValue.dateOfBirth),
      dateOfDeath: this.toIsoDayStart(formValue.dateOfDeath),
      animalSpeciesId: formValue.species?.id,
    };
    const updatedDeployment: webDtos.Deployment = {
      id: this.data.deployment?.id,
      collarId: formValue.collarId ?? this.data.collar?.id,
      animalId: formValue.animalId ?? this.data.animal?.id,
      animalName: formValue.name,
      startDate: this.toIsoDayStart(formValue.startDate),
      comment: formValue.deployComment,
      age: formValue.age,
      weight: formValue.weight,
      startLatitude: formValue.latitude,
      startLongitude: formValue.longitude,
      startPerson: formValue.deployPerson,
      endType: formValue.endType,
      endLatitude: formValue.endLatitude,
      endLongitude: formValue.endLongitude,
      endPerson: formValue.endPerson,
    };

    // Add end deployment fields if closeDeployment is checked
    if (formValue.closeDeployment) {
      updatedDeployment.endDate = this.toIsoDayEnd(formValue.endDate);
      updatedDeployment.endType = formValue.endType;
      updatedDeployment.endLatitude = formValue.endLatitude;
      updatedDeployment.endLongitude = formValue.endLongitude;
      updatedDeployment.endPerson = formValue.endPerson;
    }

    if (this.isNewAnimal()) {
      updatedAnimal.deployments = [updatedDeployment];
    }

    const result: typeof this.result = {
      animal: updatedAnimal,
      deployment: updatedDeployment,
      type: this.data.type,
      applyCollarRoles: Boolean(this.form.get('shareWithCollarUsers')?.value),
    };
    this.dialogRef.close(result);
  }

  private isNewAnimal() {
    return (
      this.data.type === 'add' ||
      (this.data.type === 'assignAnimal' && this.form.value.animalId === 0)
    );
  }

  sexTranslator(sex: webEnums.AnimalSex) {
    return Translator.animalSex(sex);
  }

  speciesTranslator(species: dsDtos.AnimalSpecies) {
    return Translator.species(species);
  }

  onSpeciesChange($event: MatSelectChange) {
    const currentName: string = this.form.get('name')?.value;
    if (
      this.isNewAnimal() &&
      (!currentName || currentName === '' || currentName.startsWith('Animal for Collar '))
    ) {
      this.form
        .get('name')
        ?.setValue(
          `${$event.value?.english} ${this.data.collar?.id ?? this.form.get('collarId')?.value}`,
        );
    }
  }

  onColorSelected(color: string) {
    this.form.get('selectedColor')?.setValue(color);
  }

  getEndTypeTranslation(endType: webEnums.EndType) {
    return Translator.deploymentEndType(endType);
  }

  getAgeTranslation(age: webEnums.Age) {
    return Translator.deploymentAge(age);
  }

  getTitle() {
    if (this.data.type === 'edit') {
      return 'Edit Animal and Deployment';
    } else if (this.data.type === 'add') {
      return 'Add Animal and Deployment';
    } else if (this.data.type === 'assignCollar') {
      return 'Assign Collar to Animal';
    } else if (this.data.type === 'assignAnimal') {
      return 'Assign Animal to Collar';
    }
  }

  getButtonTitle() {
    if (this.data.type === 'edit') {
      return 'Save';
    } else if (this.data.type === 'add') {
      return 'Create';
    } else if (this.data.type === 'assignCollar') {
      return 'Assign Collar';
    } else if (this.data.type === 'assignAnimal') {
      return 'Assign Animal';
    }
  }

  private friendlyControlNames = {
    collarId: 'Select Collar',
    animalId: 'Select Animal',
    species: 'Species',
    name: 'Name',
    startDate: 'Start Deployment',
    endDate: 'End Deployment',
    latitude: 'Start Latitude',
    longitude: 'Start Longitude',
    endLatitude: 'End Latitude',
    endLongitude: 'End Longitude',
  };

  getSubmitTooltip() {
    if (this.form.valid && this.form.dirty) {
      return '';
    }
    if (!this.form.dirty) {
      return 'No changes detected.';
    }
    const errors: string[] = [];
    for (const key of Object.keys(this.form.controls)) {
      const control = this.form.get(key);
      if (control?.invalid) {
        const friendlyName = this.friendlyControlNames[key] || key;

        if (control.hasError('required')) {
          errors.push(`${friendlyName} is required`);
        } else if (control.hasError('min') || control.hasError('max')) {
          errors.push(`${friendlyName} has an invalid value`);
        } else if (control.hasError('invalidNumericInput')) {
          errors.push(`${friendlyName} must be a valid number`);
        } else {
          errors.push(friendlyName); // Generic "invalid"
        }
      }
    }

    if (errors.length > 0) {
      // Join the first few errors for a concise message
      const errorList = errors.slice(0, 3).join(', ');
      return `Please fix the following issues: ${errorList}${errors.length > 3 ? '...' : ''}`;
    }

    // Fallback message if no specific controls are found
    return 'Please fill out all required fields correctly.';
  }

  preventInvalidCharacters(event: KeyboardEvent): void {
    // Allow navigation and control keys
    if (
      ['Backspace', 'End', 'ArrowLeft', 'ArrowRight'].includes(event.key) ||
      event.ctrlKey ||
      event.metaKey
    ) {
      return;
    }

    const input = event.target as HTMLInputElement;
    const value = input.value;

    // Allow a single decimal point
    if (event.key === '.' && !value.includes('.')) {
      return;
    }

    // Allow digits
    if (/\d/.test(event.key)) {
      return;
    }

    // Prevent all other characters
    event.preventDefault();
  }

  numericInputValidator(control: AbstractControl): ValidationErrors | null {
    if (control.value === null) {
      return control.dirty ? { invalidNumericInput: true } : null;
    }
    if (control.value === '' || control.value === undefined) {
      return null;
    }
    if (isNaN(Number(control.value))) {
      return { invalidNumericInput: true };
    }
    return null;
  }
}
